import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Loader2, Download, Upload, ImageIcon, X, RotateCcw, Check,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import Logo from "@/components/Logo";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import formatDoctorName from "@/lib/names";

// Must match the constants in backend/standee.py.
const DEFAULT_DX = 0.0;      // photo nudge inside the ring, in units of the ring radius
const DEFAULT_DY = 0.0;
const DEFAULT_SCALE = 1.0;   // 1 = the photo fills the ring edge to edge
const DEFAULT_TEXT_SCALE = 1.0;
const TEMPLATE = "/standee.png";
const TEMPLATE_RATIO = 3513 / 2486;   // height / width
// The empty ring on the template: centre x and radius as fractions of width,
// centre y as a fraction of height.
const RING_CX = 0.2615;
const RING_CY = 0.6607;
const RING_R = 0.1730;
// Text block, mirrored from backend/standee.py so the preview shows the real layout.
const TEXT_X = 0.5279;
const NAME_Y = 0.5850;
const NAME_SIZE = 0.0495;   // fractions of template WIDTH
const SUB_SIZE = 0.0334;
const SUB_GAP = 0.0145;
const TEXT_RIGHT_PAD = 0.03;
const LAYOUT = "standee-2026-11";   // positions saved for an older template are ignored

// Mirrors the backend's fit_font: shrink a long name step by step so it stays on
// one line instead of wrapping into the rule below.
function fitNamePx(name, px, availPx) {
  const canvas = fitNamePx.canvas || (fitNamePx.canvas = document.createElement("canvas"));
  const ctx = canvas.getContext("2d");
  const floor = px * 0.55;
  let size = px;
  for (;;) {
    ctx.font = `bold ${size}px Arial, Helvetica, sans-serif`;
    if (ctx.measureText(name).width <= availPx || size * 0.95 < floor) return size;
    size = size * 0.95;
  }
}

// Mirrors the backend's naming rule so the preview shows the final text.
function displayName(raw) {
  const n = (raw || "").trim();
  if (!n) return "";
  if (!n.toLowerCase().startsWith("dr")) return `Dr. ${n}`;
  if (n.toLowerCase().startsWith("dr ")) return `Dr.${n.slice(2)}`;
  return n;
}

export default function StandeeStudio() {
  const { id } = useParams();
  const navigate = useNavigate();
  const stageRef = useRef(null);
  const fileRef = useRef(null);

  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [photoUrl, setPhotoUrl] = useState("");     // object URL or server URL
  const [photoFile, setPhotoFile] = useState(null); // only set when newly chosen
  const [natural, setNatural] = useState(null);     // { w, h } of the photo
  const [pos, setPos] = useState({ dx: DEFAULT_DX, dy: DEFAULT_DY, scale: DEFAULT_SCALE });
  const [text, setText] = useState({ x: TEXT_X, y: NAME_Y, scale: DEFAULT_TEXT_SCALE });
  const [selected, setSelected] = useState("photo");   // photo | text
  const [fields, setFields] = useState({ doctor_name: "", qualification: "" });
  const [drag, setDrag] = useState(null);
  const [stageW, setStageW] = useState(420);

  // Font sizes are fractions of template width, so the preview needs its own width.
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setStageW(e.contentRect.width));
    ro.observe(el);
    setStageW(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, [loading]);

  useEffect(() => {
    let alive = true;
    api.get(`/admin/submissions/${id}`)
      .then(({ data }) => {
        if (!alive) return;
        setSub(data);
        setFields({
          doctor_name: formatDoctorName(data.doctor_name) || "",
          qualification: data.qualification || "",
        });
        // Positions saved against an earlier template would land in the wrong
        // place on this one, so only restore them when the layout matches.
        const current = data.standee_layout === LAYOUT;
        setPos({
          dx: (current && data.standee_photo_dx) || DEFAULT_DX,
          dy: (current && data.standee_photo_dy) || DEFAULT_DY,
          scale: (current && data.standee_photo_scale) || DEFAULT_SCALE,
        });
        setText({
          x: (current && data.standee_text_x) || TEXT_X,
          y: (current && data.standee_text_y) || NAME_Y,
          scale: (current && data.standee_text_scale) || DEFAULT_TEXT_SCALE,
        });
        // Start from the photo already on file (the one set here, or the one the
        // manager submitted), so the admin only adjusts it or swaps it out.
        if (data.standee_photo || data.doctor_photo) {
          const token = localStorage.getItem("bc_token");
          setPhotoUrl(`${API}/admin/submissions/${id}/standee/photo?auth=${token}&t=${Date.now()}`);
        }
      })
      .catch((err) => {
        if (alive) toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load submission");
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [id]);

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("Image must be under 8MB"); return; }
    setPhotoFile(f);
    setPhotoUrl((old) => { if (old?.startsWith("blob:")) URL.revokeObjectURL(old); return URL.createObjectURL(f); });
    setNatural(null);
  };

  // Drag moves the photo inside the ring (in units of the ring radius) or the name
  // block (in template fractions); both are what the renderer uses, so the preview
  // and the rendered PNG agree exactly.
  const startDrag = (what) => (e) => {
    if (what === "photo" && !photoUrl) return;
    e.preventDefault();
    e.stopPropagation();
    setSelected(what);
    const r = stageRef.current.getBoundingClientRect();
    const from = what === "photo" ? { a: pos.dx, b: pos.dy } : { a: text.x, b: text.y };
    setDrag({ what, x: e.clientX, y: e.clientY, a: from.a, b: from.b, w: r.width, h: r.height });
  };
  const onPointerMove = useCallback((e) => {
    if (!drag) return;
    if (drag.what === "photo") {
      const radius = drag.w * RING_R;
      const a = Math.min(2, Math.max(-2, drag.a + (e.clientX - drag.x) / radius));
      const b = Math.min(2, Math.max(-2, drag.b + (e.clientY - drag.y) / radius));
      setPos((p) => ({ ...p, dx: a, dy: b }));
    } else {
      const a = Math.min(1, Math.max(0, drag.a + (e.clientX - drag.x) / drag.w));
      const b = Math.min(1, Math.max(0, drag.b + (e.clientY - drag.y) / drag.h));
      setText((t) => ({ ...t, x: a, y: b }));
    }
  }, [drag]);
  const onPointerUp = () => setDrag(null);

  useEffect(() => {
    if (!drag) return;
    const move = (e) => onPointerMove(e);
    const up = () => setDrag(null);
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
  }, [drag, onPointerMove]);

  // Mirror the backend's circle_photo exactly, in stage pixels: at scale 1 the
  // shorter side of the photo equals the ring's diameter, and the photo centre sits
  // on the ring centre plus the nudge.
  const previewBox = () => {
    if (!natural) return null;
    const radius = stageW * RING_R;
    const k = ((2 * radius) / Math.min(natural.w, natural.h)) * pos.scale;
    const w = natural.w * k;
    const h = natural.h * k;
    return {
      w, h,
      left: radius + pos.dx * radius - w / 2,
      top: radius + pos.dy * radius - h / 2,
    };
  };

  const save = async () => {
    if (!photoUrl) { toast.error("Please choose a doctor photo first."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      if (photoFile) fd.append("file", photoFile);
      fd.append("doctor_name", fields.doctor_name);
      fd.append("qualification", fields.qualification);
      fd.append("photo_dx", String(pos.dx));
      fd.append("photo_dy", String(pos.dy));
      fd.append("photo_scale", String(pos.scale));
      fd.append("text_x", String(text.x));
      fd.append("text_y", String(text.y));
      fd.append("text_scale", String(text.scale));
      const { data } = await api.post(`/admin/submissions/${id}/standee`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSub((s) => ({ ...s, ...data, standee_status: data.standee_status }));
      setPhotoFile(null);
      toast.success("Standee saved with this position.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save the standee");
    } finally {
      setBusy(false);
    }
  };

  const download = () => {
    const token = localStorage.getItem("bc_token");
    window.open(`${API}/admin/submissions/${id}/standee/download?auth=${token}`, "_blank");
  };
  // The lama is rendered from the same photo and framing the save just stored.
  const downloadLama = () => {
    const token = localStorage.getItem("bc_token");
    window.open(`${API}/admin/submissions/${id}/lama?auth=${token}`, "_blank");
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#F8F9FA]">
        <Loader2 className="h-8 w-8 animate-spin text-[#0B5351]" />
      </div>
    );
  }
  if (!sub) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#F8F9FA] px-6">
        <div className="text-center">
          <p className="font-heading font-bold text-slate-900 mb-4">Submission not found</p>
          <Button onClick={() => navigate("/admin")} className="rounded-xl bg-[#0B5351] h-11 px-6">Back</Button>
        </div>
      </div>
    );
  }

  const box = previewBox();
  const saved = sub.standee_status && sub.standee_status !== "None";
  const shownName = displayName(fields.doctor_name);
  const namePx = fitNamePx(shownName, NAME_SIZE * text.scale * stageW, (1 - text.x - TEXT_RIGHT_PAD) * stageW);

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <header className="bg-white/80 backdrop-blur-xl border-b border-slate-200/60 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Logo className="h-9 w-9" />
            <div className="min-w-0">
              <p className="font-heading font-bold text-slate-900 leading-none truncate">Standee Studio</p>
              <p className="text-xs text-slate-500 truncate">{formatDoctorName(sub.doctor_name)}</p>
            </div>
          </div>
          <Button data-testid="studio-back" onClick={() => navigate("/admin")} variant="outline" size="sm"
            className="rounded-lg h-11 px-3 shrink-0">
            <ArrowLeft className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">Back</span>
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-8 items-start">
        {/* Preview */}
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">Preview</p>
          <h1 className="font-heading text-2xl font-black tracking-tighter text-slate-900 mb-1">
            Drag to position
          </h1>
          <p className="text-slate-500 mb-4 text-sm">
            Drag the photo inside the ring or move the name block; what you see is what gets rendered at full print size, on the standee and on the lama.
          </p>
          <div
            ref={stageRef}
            data-testid="standee-stage"
            className="relative mx-auto w-full max-w-[420px] rounded-xl overflow-hidden border border-slate-200 bg-white shadow-sm select-none"
            style={{ aspectRatio: `1 / ${TEMPLATE_RATIO}`, "--stage-w": `${stageW}px` }}
          >
            <img src={TEMPLATE} alt="Standee template" className="absolute inset-0 w-full h-full" draggable={false} />
            {/* Name block — draggable, drawn from the same fractions the renderer
                uses so the preview and the output agree. */}
            <div
              data-testid="stage-text"
              onPointerDown={startDrag("text")}
              className={`absolute ${drag?.what === "text" ? "cursor-grabbing" : "cursor-grab"} rounded-sm transition-shadow ${
                selected === "text" ? "ring-2 ring-[#E63946]/60" : "ring-1 ring-transparent hover:ring-[#E63946]/40"
              }`}
              style={{
                left: `${text.x * 100}%`,
                top: `${text.y * 100}%`,
                width: `${(1 - text.x - TEXT_RIGHT_PAD) * 100}%`,
                padding: "1px",
                zIndex: selected === "text" ? 20 : 10,
              }}
            >
              <div data-testid="stage-name" style={{
                pointerEvents: "none",
                color: "#e31e2b", fontWeight: "bold", lineHeight: 1.05,
                fontSize: `${namePx}px`,
                fontFamily: "Arial, Helvetica, sans-serif",
              }}>
                {shownName}
              </div>
              <div style={{ height: `calc(${SUB_GAP * text.scale} * var(--stage-w, 420px))` }} />
              {fields.qualification && (
                <div style={{
                  pointerEvents: "none",
                  color: "#1e347d", lineHeight: 1.15,
                  fontSize: `calc(${SUB_SIZE * text.scale} * var(--stage-w, 420px))`,
                  fontFamily: "Arial, Helvetica, sans-serif",
                }}>{fields.qualification}</div>
              )}
            </div>

            {/* The ring: the photo is clipped to the ring's inner edge, exactly as the
                renderer does, and dragging moves it inside the ring. */}
            {photoUrl && (
              <div
                data-testid="stage-ring"
                onPointerDown={startDrag("photo")}
                className={`absolute overflow-hidden rounded-full ${drag?.what === "photo" ? "cursor-grabbing" : "cursor-grab"} ${
                  selected === "photo" ? "ring-2 ring-[#0B5351]/60" : "ring-1 ring-transparent hover:ring-[#0B5351]/40"
                }`}
                style={{
                  zIndex: selected === "photo" ? 20 : 10,
                  left: `${(RING_CX - RING_R) * 100}%`,
                  top: `${RING_CY * 100}%`,
                  width: `${RING_R * 2 * 100}%`,
                  aspectRatio: "1 / 1",
                  transform: "translateY(-50%)",
                }}
              >
                <img
                  src={photoUrl}
                  alt="Doctor"
                  data-testid="stage-photo"
                  draggable={false}
                  onLoad={(e) => setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
                  className="absolute max-w-none pointer-events-none"
                  style={box ? {
                    width: `${box.w}px`,
                    height: `${box.h}px`,
                    left: `${box.left}px`,
                    top: `${box.top}px`,
                  } : { visibility: "hidden" }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Controls */}
        <div className="space-y-5">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4">
            <div className="space-y-2">
              <Label>Doctor Photo</Label>
              <input ref={fileRef} type="file" accept="image/*" onChange={pickFile}
                className="hidden" data-testid="studio-file-input" />
              {photoUrl ? (
                <div className="flex items-center gap-2">
                  <Button variant="outline" data-testid="studio-change-photo"
                    onClick={() => fileRef.current?.click()} className="rounded-lg h-11 flex-1">
                    <Upload className="h-4 w-4 mr-2" /> Change photo
                  </Button>
                  <Button variant="outline" size="sm" data-testid="studio-clear-photo"
                    onClick={() => { setPhotoUrl(""); setPhotoFile(null); setNatural(null); if (fileRef.current) fileRef.current.value = ""; }}
                    className="rounded-lg h-11 px-3">
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <button type="button" data-testid="studio-pick-photo" onClick={() => fileRef.current?.click()}
                  className="w-full h-28 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 hover:border-[#0B5351] hover:bg-[#0B5351]/5 grid place-items-center transition-colors">
                  <div className="text-center">
                    <ImageIcon className="h-6 w-6 text-slate-400 mx-auto mb-1.5" />
                    <p className="text-sm font-medium text-slate-600">Upload doctor photo</p>
                  </div>
                </button>
              )}
              <p className="text-xs text-slate-500">A head-and-shoulders photo works best; it is cropped to the circle.</p>
            </div>

            <div className="space-y-2">
              <Label>Adjusting</Label>
              <div className="flex p-1 rounded-lg bg-slate-100 border border-slate-200">
                {[["photo", "Photo"], ["text", "Name block"]].map(([k, lbl]) => (
                  <button key={k} type="button" data-testid={`select-${k}`}
                    onClick={() => setSelected(k)}
                    className={`flex-1 h-9 rounded-md text-sm font-semibold transition-colors ${
                      selected === k ? "bg-white text-[#0B5351] shadow-sm" : "text-slate-500"
                    }`}>
                    {lbl}
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-500">Drag either one directly on the preview.</p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="size">{selected === "photo" ? "Photo size" : "Text size"}</Label>
                <span className="text-xs text-slate-500 tabular-nums">
                  {Math.round((selected === "photo" ? pos.scale : text.scale) * 100)}%
                </span>
              </div>
              <Slider id="size" data-testid="studio-scale"
                min={selected === "photo" ? 100 : 50} max={selected === "photo" ? 300 : 180} step={1}
                value={[Math.round((selected === "photo" ? pos.scale : text.scale) * 100)]}
                onValueChange={([v]) => {
                  if (selected === "photo") setPos((p) => ({ ...p, scale: v / 100 }));
                  else setText((t) => ({ ...t, scale: v / 100 }));
                }} />
            </div>

            <Button variant="outline" data-testid="studio-reset" className="w-full rounded-lg h-10"
              onClick={() => {
                if (selected === "photo") setPos({ dx: DEFAULT_DX, dy: DEFAULT_DY, scale: DEFAULT_SCALE });
                else setText({ x: TEXT_X, y: NAME_Y, scale: DEFAULT_TEXT_SCALE });
              }}>
              <RotateCcw className="h-4 w-4 mr-2" /> Reset {selected === "photo" ? "photo" : "name block"}
            </Button>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="dn">Doctor Name</Label>
              <Input id="dn" data-testid="studio-doctor-name" className="h-11 bg-slate-50"
                value={fields.doctor_name}
                onChange={(e) => setFields({ ...fields, doctor_name: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dq">Qualification</Label>
              <Input id="dq" data-testid="studio-qualification" className="h-11 bg-slate-50"
                value={fields.qualification}
                onChange={(e) => setFields({ ...fields, qualification: e.target.value })} />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Button data-testid="studio-save" onClick={save} disabled={busy || !photoUrl}
              className="w-full rounded-xl h-12 bg-[#0B5351] hover:bg-[#094241] font-semibold">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Check className="h-4 w-4 mr-2" /> Save Standee</>}
            </Button>
            {saved && (
              <>
                <Button variant="outline" data-testid="studio-download" onClick={download}
                  className="w-full rounded-xl h-12">
                  <Download className="h-4 w-4 mr-2" /> Download standee
                </Button>
                <Button variant="outline" data-testid="studio-download-lama" onClick={downloadLama}
                  className="w-full rounded-xl h-12">
                  <Download className="h-4 w-4 mr-2" /> Download lama
                </Button>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
